import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaCalendarAlt, FaImage, FaSpinner } from 'react-icons/fa';
import { API_URL } from '../../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  NO_CACHE_JSON_HEADERS,
  subscribeToContentUpdates,
} from '../../utils/contentSync';
import { getRichTextPlainText, sanitizeRichTextHtml } from '../../utils/richText';
import './NewsDetail.css';

const formatNewsDate = (value = '') => {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(`${value}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsedDate);
};

const normalizeNewsRecord = (record = {}) => ({
  id: record.id ?? record._id ?? '',
  title: record.title || '',
  description: record.description || record.content || record.shortDescription || '',
  imageUrl: record.imageUrl || record.image_url || '',
  date: record.date || '',
});

const getPlainNewsText = (value = '') => getRichTextPlainText(value).replace(/\s+/g, ' ').trim();

const NewsDetail = () => {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let isMounted = true;

    const fetchNewsItem = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await fetch(buildFreshUrl(`${API_URL.NEWS}/${id}`), {
          cache: 'no-store',
          headers: {
            ...NO_CACHE_JSON_HEADERS,
          },
        });
        const data = await response.json().catch(() => null);

        if (!response.ok || !data?.success) {
          throw new Error(data?.message || 'Unable to load this news item.');
        }

        if (isMounted) {
          setItem(normalizeNewsRecord(data.data));
        }
      } catch (fetchError) {
        console.error('Error loading news item:', fetchError);

        if (isMounted) {
          setError(fetchError.message || 'Unable to load this news item.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchNewsItem();

    return () => {
      isMounted = false;
    };
  }, [id, refreshToken]);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [CONTENT_SYNC_KEYS.NEWS],
        onUpdate: () => {
          setRefreshToken((current) => current + 1);
        },
      }),
    []
  );

  return (
    <div className="news-detail-page">
      <div className="news-detail-container">
        <motion.div
          className="news-detail-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <Link to="/news-blogs" className="news-detail-back">
            <FaArrowLeft />
            Back to News & Blogs
          </Link>

          {loading ? (
            <div className="news-detail-state">
              <FaSpinner className="spin" />
              <p>Loading news item...</p>
            </div>
          ) : error ? (
            <div className="news-detail-state error">
              <p>{error}</p>
            </div>
          ) : !item ? (
            <div className="news-detail-state">
              <FaImage />
              <p>No news item found.</p>
            </div>
          ) : (
            <div className="news-detail-content">
              {item.date && (
                <div className="news-detail-meta">
                  <span className="news-detail-date">
                    <FaCalendarAlt />
                    {formatNewsDate(item.date)}
                  </span>
                </div>
              )}
              <h1
                dangerouslySetInnerHTML={{
                  __html: sanitizeRichTextHtml(item.title, { wrapPlainText: false }),
                }}
              />
              {item.imageUrl && <img src={item.imageUrl} alt={getPlainNewsText(item.title) || 'News image'} />}
              {item.description && (
                <div
                  className="news-detail-description"
                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(item.description) }}
                />
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default NewsDetail;
